import { useState, useEffect, useCallback } from 'react';
import teamRepositoryService from '../services/teamRepositoryService';
import { notify } from '../components/CustomToast';

const useTeamRepository = (teamId) => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    const fetchFiles = useCallback(async (page = 1, isLoadMore = false) => {
        try {
            if (isLoadMore) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }

            const res = await teamRepositoryService.getFiles(teamId, { per_page: 12, page });
            const newFiles = res?.data?.files || [];

            if (isLoadMore) {
                setFiles(prev => [...prev, ...newFiles]);
            } else {
                setFiles(newFiles);
            }

            if (res?.data?.pagination) {
                const { current_page, last_page } = res.data.pagination;
                setCurrentPage(current_page);
                setHasMore(current_page < last_page);
            } else {
                setHasMore(false);
            }
        } catch {
            notify('error', 'Failed to load repository files');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [teamId]);

    useEffect(() => {
        fetchFiles(1, false);
    }, [fetchFiles]);

    const loadMore = useCallback(() => {
        if (!loadingMore && hasMore) {
            fetchFiles(currentPage + 1, true);
        }
    }, [loadingMore, hasMore, currentPage, fetchFiles]);

    const refetch = useCallback(() => {
        setCurrentPage(1);
        setHasMore(true);
        return fetchFiles(1, false);
    }, [fetchFiles]);

    const prependFile = (file) => setFiles(prev => [file, ...prev]);
    const removeFile = (id) => setFiles(prev => prev.filter(f => f.id !== id));

    return { files, loading, loadingMore, hasMore, loadMore, refetch, prependFile, removeFile };
};

export default useTeamRepository;
